import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Check, ChevronDown, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  SortableList,
  Progress,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { cn } from '@/lib/cn';
import { useUndo } from '@/design/components';
import {
  checklistState,
  minutesNowIn,
  STATE_FILTERS,
  STATE_LABEL,
  STATE_TONE,
  type ChecklistState,
} from '../checklistStatus';
import { formatDateTime, WEEKDAYS } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { useEmployeeOptions } from '@/features/employees/api';
import { useChecklistStatus } from '../api';
import {
  fetchTemplateItems,
  useTemplateItems,
  useTemplates,
  useTemplateMutations,
} from './api';
import { TemplateDialog } from './TemplateDialog';
import {
  branchesLabel,
  EMPTY_TEMPLATE,
  recurrenceLabel,
  type TemplateValues,
} from './templateValues';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/** תבניות הצ'קליסט, ומה הושלם היום (PRD 5.9.1). */
export function ChecklistsAdminPage() {
  // נקרא פעם אחת לרינדור: הדיוק שנדרש כאן הוא דקות, ושעון שרץ
  // היה מרנדר מחדש את כל הטבלה בלי סיבה
  const minutesNow = minutesNowIn(new Date());
  const canManage = usePerm('ops.manage');
  const templates = useTemplates();
  const status = useChecklistStatus();
  const { data: options } = useEmployeeOptions();
  const mutations = useTemplateMutations();
  const [branchFilter, setBranchFilter] = useState('');
  /** מצבים מוצגים. ריק = הכל. */
  const [stateFilter, setStateFilter] = useState<ChecklistState[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  /** התבנית שפתוחה בחלון. null = סגור. */
  const [dialog, setDialog] = useState<TemplateValues | null>(null);

  /** פתיחת תבנית לעריכה לפי מזהה, גם מטבלת המצב היומית. */
  function openTemplate(id: string) {
    const template = templates.data?.find((one) => one.id === id);
    if (!template) return;

    // המשימות נטענות לפני הפתיחה, כדי שהחלון ייוולד מלא ולא
    // יזרע את עצמו מתוך effect
    void fetchTemplateItems(id)
      .then((items) => setDialog(toValues(template, items)))
      .catch((caught: unknown) => setError(errorMessage(caught)));
  }

  function toValues(
    template: NonNullable<typeof templates.data>[number],
    items: { id: string; text: string; requires_photo: boolean }[],
  ): TemplateValues {
    return {
      id: template.id,
      name: template.name,
      branchIds: template.checklist_template_branches.map((one) => one.branch_id),
      recurrence: template.recurrence,
      weekdays: template.weekdays ?? [],
      dayOfMonth: template.day_of_month,
      onDate: template.on_date,
      dueTime: template.due_time,
      items,
    };
  }
  const undo = useUndo();
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  return (
    <AdminPage title="צ׳קליסטים" subtitle="מה צריך לעשות בפתיחה ובסגירה">
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <Panel
        title="מה הושלם היום"
        actions={
          <div className="flex flex-wrap items-center gap-3">
            {/*
              בחירה מרובה: "מה לא נסגר" ו"מה באיחור" הן שתי שאלות
              שנשאלות יחד, ובורר יחיד היה מכריח לבחור ביניהן.
            */}
            <div className="flex flex-wrap gap-1">
              {STATE_FILTERS.map((option) => {
                const active = stateFilter.includes(option.state);
                return (
                  <button
                    key={option.state}
                    type="button"
                    aria-pressed={active}
                    onClick={() =>
                      setStateFilter((current) =>
                        current.includes(option.state)
                          ? current.filter((one) => one !== option.state)
                          : [...current, option.state],
                      )
                    }
                    className={cn(
                      'min-h-11 cursor-pointer rounded-pill border px-3 text-label transition-colors',
                      active
                        ? 'border-ink bg-olive font-semibold text-ink'
                        : 'border-muted bg-transparent font-medium text-ink hover:bg-cream',
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            {(options?.branches ?? []).length > 1 ? (
            <label className="flex items-center gap-2">
              <span className="text-label font-medium">סניף</span>
              <select
                value={branchFilter}
                onChange={(event) => setBranchFilter(event.target.value)}
                className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body-sm text-ink"
              >
                <option value="">כל הסניפים</option>
                {(options?.branches ?? []).map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              </label>
            ) : null}
          </div>
        }
      >
        {status.isPending && <Skeleton className="h-24" />}
        {status.data?.length === 0 && (
          <p className="m-0 text-body-sm text-muted">אין צ׳קליסטים פעילים.</p>
        )}
        {status.data && status.data.length > 0 && (() => {
          const rows = status.data.filter((row) => {
            if (branchFilter && row.branch_id !== branchFilter) return false;
            if (stateFilter.length === 0) return true;

            const state = checklistState({
              completedAt: row.completed_at,
              done: row.done,
              total: row.total,
              dueTime: row.due_time,
              minutesNow,
            });
            // "לא התחיל" מכסה גם תבנית בלי מועד, כי זו התווית
            // היחידה שהמשתמש רואה לשניהם
            if (state === 'unscheduled') return stateFilter.includes('late');
            return stateFilter.includes(state);
          });

          return rows.length === 0 ? (
            <EmptyState
              title="אין צ׳קליסטים לסניף הזה"
              description="אפשר להגדיר תבנית לסניף למטה."
            />
          ) : (
          <Table>
            <thead>
              <Tr>
                <Th>סניף</Th>
                <Th>צ׳קליסט</Th>
                <Th>התקדמות</Th>
                <Th>מי</Th>
                <Th>מצב</Th>
              </Tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const state = checklistState({
                  completedAt: row.completed_at,
                  done: row.done,
                  total: row.total,
                  dueTime: row.due_time,
                  minutesNow,
                });
                const tone = STATE_TONE[state];

                return (
                  <Tr key={`${row.branch_id}-${row.template_id}`}>
                    <Td>{row.branch}</Td>
                    <Td>
                      <span className="grid gap-0.5">
                        {/*
                          שם הצ'קליסט פותח את התבנית שלו. מי שרואה
                          כאן שמשהו לא בסדר רוצה לתקן אותו מכאן,
                          ולא לחפש אותו שוב ברשימה למטה.
                        */}
                        <button
                          type="button"
                          onClick={() => openTemplate(row.template_id)}
                          className="-m-1 cursor-pointer rounded-sm bg-transparent p-1 text-start text-ink underline-offset-2 hover:bg-cream hover:underline"
                        >
                          {row.template}
                        </button>
                        {row.due_time && (
                          <span className="tabular text-label text-muted" dir="ltr">
                            {row.due_time.slice(0, 5)}
                          </span>
                        )}
                      </span>
                    </Td>
                    <Td className="min-w-40">
                      {/*
                        מד ולא "0 / 3": אותו דפוס כמו במכירות
                        ובשעות, והמילוי נקרא במבט אחד.
                      */}
                      {row.total > 0 ? (
                        <Progress
                          value={row.done}
                          max={row.total}
                          label={`${row.done} מתוך ${row.total}`}
                        />
                      ) : (
                        <span className="text-label text-muted">אין משימות</span>
                      )}
                    </Td>
                    <Td className="text-muted">{row.by ?? '—'}</Td>
                    <Td>
                      {row.completed_at ? (
                        <StatusBadge tone="good">{formatDateTime(row.completed_at)}</StatusBadge>
                      ) : tone ? (
                        <StatusBadge tone={tone}>{STATE_LABEL[state]}</StatusBadge>
                      ) : (
                        // "ממתין" אינו חדשות, ולכן אינו נצבע
                        <span className="text-body-sm text-muted">{STATE_LABEL[state]}</span>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
          );
        })()}
      </Panel>

      <Panel
        title="תבניות"
        actions={
          <Button variant="primary" onClick={() => setDialog(EMPTY_TEMPLATE)}>
            <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
            תבנית חדשה
          </Button>
        }
      >

        {templates.isPending && <Skeleton className="h-24" />}
        {templates.isError && <ErrorState onRetry={() => void templates.refetch()} />}
        {templates.data?.length === 0 && (
          <EmptyState title="אין תבניות" description="תבנית היא רשימת המשימות של המשמרת." />
        )}

        <div className="grid gap-2">
          {templates.data?.filter((template) => !undo.isPending(template.id)).map((template) => (
            <div key={template.id} className="rounded-md border border-line">
              <div className="flex flex-wrap items-center gap-2 p-3">
                  {/* השורה נפתחת בלחיצה, ולכן היא צריכה להיראות כמו
                      משהו שלוחצים עליו: חץ, סמן עכבר ושינוי ברקע */}
                  <button
                    type="button"
                    onClick={() => setOpenId(openId === template.id ? null : template.id)}
                    aria-expanded={openId === template.id}
                    className="-m-1 flex grow cursor-pointer items-center gap-2 rounded-sm bg-transparent p-1 text-start text-body font-medium text-ink hover:bg-cream"
                  >
                    <ChevronDown
                      size={18}
                      strokeWidth={1.75}
                      aria-hidden="true"
                      className={cn(
                        'shrink-0 text-muted transition-transform',
                        openId === template.id && 'rotate-180',
                      )}
                    />
                    {template.name}
                  </button>
                  {/*
                    מה שנבחר בחלון צריך להיראות גם ברשימה. בלי זה
                    אי אפשר לדעת למי התבנית שייכת ומתי היא רצה בלי
                    לפתוח אותה.
                  */}
                  <span className="flex flex-wrap items-center gap-1 text-label text-muted">
                    <span>{branchesLabel(template)}</span>
                    <span aria-hidden="true">·</span>
                    <span>{recurrenceLabel(template, WEEKDAYS)}</span>
                    {template.due_time && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span className="tabular" dir="ltr">
                          {template.due_time.slice(0, 5)}
                        </span>
                      </>
                    )}
                  </span>
                  <Button
                    variant="ghost"
                    aria-label={`עריכת ${template.name}`}
                    onClick={() => openTemplate(template.id)}
                  >
                    <Pencil size={20} strokeWidth={1.75} aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    aria-label={`מחיקת ${template.name}`}
                    onClick={() =>
                      undo.remove({
                        id: template.id,
                        message: `"${template.name}" נמחקה`,
                        perform: () =>
                          mutations.removeTemplate
                            .mutateAsync(template.id)
                            .catch((caught: unknown) => {
                              setError(errorMessage(caught));
                              throw caught;
                            }),
                      })
                    }
                  >
                    <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                  </Button>
              </div>

              {openId === template.id && <Items templateId={template.id} />}
            </div>
          ))}
        </div>
      </Panel>
      {dialog && (
        <TemplateDialog
          initial={dialog}
          branches={options?.branches ?? []}
          isSaving={mutations.saveTemplate.isPending}
          onClose={() => setDialog(null)}
          onSave={async (values) => {
            const id = await mutations.saveTemplate.mutateAsync(values);
            await mutations.saveItems.mutateAsync({ templateId: id, items: values.items });
          }}
        />
      )}
    </AdminPage>
  );
}

function Items({ templateId }: { templateId: string }) {
  const items = useTemplateItems(templateId);
  const mutations = useTemplateMutations();
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const undo = useUndo();

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action()
      .then(() => void items.refetch())
      .catch((caught) => setError(errorMessage(caught)));
  };

  const list = (items.data ?? []).filter((item) => !undo.isPending(item.id));

  return (
    <div className="grid gap-2 border-t border-line p-3">
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {list.length > 1 && (
        <p className="m-0 text-label text-muted">אפשר לגרור משימה בידית כדי לשנות את הסדר.</p>
      )}

      <SortableList
        items={list}
        className="grid gap-1"
        onReorder={(ids) => run(() => mutations.reorderItems.mutateAsync({ templateId, ids }))}
        renderItem={(item) =>
          editingId === item.id ? (
            <ItemForm
              item={item}
              onCancel={() => setEditingId(null)}
              onSubmit={async (text) => {
                await mutations.updateItem.mutateAsync({
                  id: item.id,
                  text,
                  requires_photo: item.requires_photo,
                });
                setEditingId(null);
              }}
            />
          ) : (
            <ItemRow
              item={item}
              onEdit={() => setEditingId(item.id)}
              onTogglePhoto={(requires_photo) =>
                mutations.updateItem.mutateAsync({
                  id: item.id,
                  text: item.text,
                  requires_photo,
                })
              }
              onRemove={() =>
                undo.remove({
                  id: item.id,
                  message: 'המשימה נמחקה',
                  perform: () => mutations.removeItem.mutateAsync(item.id),
                })
              }
            />
          )
        }
      />

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const formElement = event.currentTarget;
          const form = new FormData(formElement);
          const text = String(form.get('text') ?? '').trim();
          if (!text) return;
          run(async () => {
            await mutations.addItem.mutateAsync({
              template_id: templateId,
              text,
              requires_photo: form.get('photo') === 'on',
              sort_order: list.length + 1,
            });
            formElement.reset();
          });
        }}
      >
        <input name="text" placeholder="משימה חדשה" className={`${FIELD} w-64 max-w-full`} />
        <label className="flex min-h-11 items-center gap-2 text-body-sm">
          <input type="checkbox" name="photo" className="size-5" />
          דורש צילום
        </label>
        <Button type="submit">
          <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
          הוספה
        </Button>
      </form>
    </div>
  );
}


/** עריכת נוסח המשימה. דרישת הצילום נמצאת על השורה עצמה. */
function ItemForm({
  item,
  onCancel,
  onSubmit,
}: {
  item: { id: string; text: string };
  onCancel: () => void;
  onSubmit: (text: string) => Promise<void>;
}) {
  const [text, setText] = useState(item.text);
  const [isSaving, setIsSaving] = useState(false);

  return (
    <form
      className="flex flex-wrap items-center gap-2 py-1"
      onSubmit={(event) => {
        event.preventDefault();
        if (text.trim() === '') return;
        setIsSaving(true);
        void onSubmit(text.trim()).finally(() => setIsSaving(false));
      }}
    >
      <label htmlFor={`item-${item.id}`} className="sr-only">
        נוסח המשימה
      </label>
      <input
        id={`item-${item.id}`}
        value={text}
        onChange={(event) => setText(event.target.value)}
        className={`${FIELD} w-64 max-w-full grow`}
      />
      <Button type="submit" variant="primary" disabled={isSaving || text.trim() === ''}>
        <Check size={18} strokeWidth={1.75} aria-hidden="true" />
        שמירה
      </Button>
      <Button variant="secondary" onClick={onCancel}>
        <X size={18} strokeWidth={1.75} aria-hidden="true" />
        ביטול
      </Button>
    </form>
  );
}

/**
 * שורת פריט. דרישת הצילום היא מתג חי ולא תווית: סימון שלה הוא
 * פעולה יומיומית, ולהיכנס בשבילה למצב עריכה זה שלושה קליקים לאחד.
 *
 * המתג מציג את מה שנלחץ ולא את מה שהשרת עוד מחזיר. בלי זה הוא
 * קופץ חזרה למצב הישן עד שהרענון חוזר, וברשת סלולרית זה נראה
 * כאילו הלחיצה לא נקלטה.
 */
function ItemRow({
  item,
  onEdit,
  onTogglePhoto,
  onRemove,
}: {
  item: { id: string; text: string; requires_photo: boolean };
  onEdit: () => void;
  onTogglePhoto: (value: boolean) => Promise<unknown>;
  onRemove: () => void;
}) {
  const [pending, setPending] = useState<boolean | null>(null);

  return (
    <div className="flex flex-wrap items-center gap-2 py-1">
      <span className="grow text-body">{item.text}</span>

      <label className="flex min-h-11 cursor-pointer items-center gap-2 text-body-sm text-muted">
        <input
          type="checkbox"
          checked={pending ?? item.requires_photo}
          onChange={(event) => {
            const next = event.target.checked;
            setPending(next);
            void onTogglePhoto(next).finally(() => setPending(null));
          }}
          className="size-5"
        />
        דורש צילום
      </label>

      <Button variant="ghost" aria-label="עריכה" onClick={onEdit}>
        <Pencil size={18} strokeWidth={1.75} aria-hidden="true" />
      </Button>
      <Button variant="ghost" aria-label="מחיקה" onClick={onRemove}>
        <Trash2 size={18} strokeWidth={1.75} aria-hidden="true" />
      </Button>
    </div>
  );
}
